import { useNavigate } from 'react-router';
import { localDate, type DateRange, type LocalDate, type WeekStart } from '@tahi/core';
import { useData } from '../../data/hooks';
import type { LocalDb } from '../../data/localDb';
import * as repo from '../../data/repos/spending';
import { useQuery } from '../../data/useQuery';
import { DEFAULT_WEEK_START } from './format';

/** v0.1c's useQuery (re-runs when one of the tables changes), returning just the data (undefined while loading). */
function useRead<T>(
  tables: readonly string[],
  read: (db: LocalDb, userId: string) => Promise<T>,
  deps: readonly unknown[],
): T | undefined {
  const { userId } = useData();
  return useQuery(tables, (db) => read(db, userId), [userId, ...deps]).data;
}

export const useCategories = () => useRead(['categories'], repo.listCategories, []);

export const useEntries = (range: DateRange) =>
  useRead(['ledger_entries', 'categories'], (db, u) => repo.listEntriesInRange(db, u, range), [
    range.start,
    range.end,
  ]);

export const useEntry = (id: string) =>
  useRead(['ledger_entries', 'categories'], (db, u) => repo.getEntry(db, u, id), [id]);

export const useEntryHistory = (id: string) =>
  useRead(['history'], (db, u) => repo.listEntryHistory(db, u, id), [id]);

export const useNoteHistory = () =>
  useRead(['ledger_entries'], (db, u) => repo.listNoteHistory(db, u), []);

export const useUncategorized = () =>
  useRead(['ledger_entries', 'categories'], repo.listUncategorized, []);

export const useEntryCount = () => useRead(['ledger_entries'], repo.countSpendingEntries, []);

/** The user's week start (user_settings.weekStart); Monday until it loads or if no row exists. */
export function useWeekStart(): WeekStart {
  return useRead(['user_settings'], repo.getUserSettings, [])?.weekStart ?? DEFAULT_WEEK_START;
}

/** Today in the user's time zone (Asia/Manila for Ivan), from v0.1c's data context. */
export function useToday(): LocalDate {
  return localDate(new Date(), useData().tz);
}

/** Back if there is in-app history, otherwise to `fallback` (deep links). */
export function useGoBack(fallback = '/m/spending'): () => void {
  const navigate = useNavigate();
  return () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  };
}
